import {finalize} from 'rxjs/operators';
import { Component, OnInit, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { CLUser } from './user';
import { CLUserService } from './user.service';
import { NotifierService, DialogService, TOption } from '../../ui';
import { CLUserRolePipe } from './user-role.pipe';
import { CLAccount } from './account';
import { CLAccountSearchDialogComponent } from './account-search-dialog.component';

type ViewMode = 'view' | 'new' | 'edit';

@Component({
  templateUrl: './user-edit.component.html',
})
export class CLUserEditComponent implements OnInit {

  user: CLUser

  mode: ViewMode
  title: string

  userRoleOptions: TOption[] = [
    {
      value: 'OWNER',
      text: new CLUserRolePipe().transform('OWNER'),
    },
    {
      value: 'SIMPLE',
      text: new CLUserRolePipe().transform('SIMPLE'),
    },
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private userService: CLUserService,
    private notifier: NotifierService,
    private dialogService: DialogService
  ) { }

  ngOnInit() {

    this.mode = this.route.routeConfig.data.mode;
    this.title = this.route.routeConfig.data.title;

    switch (this.mode) {
      case 'new':
        {
          this.user = new CLUser({
            username: '',
            password: '',
            firstname: '',
            lastname: '',
            role: 'OWNER',
            timezone: 'Europe/Paris',
          });
        }
        break;

      case 'edit':
        {
          // Get the user
          this.notifier.startProgress();

          this.route.data.subscribe((data) => {
            this.user = data.user;

            this.notifier.endProgress();
          });
        }
        break;

      case 'view':
        {
          // Get the user
          this.notifier.startProgress();

          this.route.data.subscribe((data) => {
            this.user = data.user;

            this.notifier.endProgress();
          });
        }
        break;
    }
  }

  save() {
    if (!this.user || !this.user.valid) {
      return;
    }

    let notifier = this.notifier;

    notifier.updateProgress(20);

    function onComplete() {
      notifier.endProgress();
    }

    switch (this.mode) {
      case 'new':
        {
          this.userService
            .create(this.user).pipe(
            finalize(onComplete))
            .subscribe((user) => {
              this.user = user;

              this.notifier.success('Utilisateur client créé avec succès');

              // Got to view screen
              this.router.navigate(['shares-ledger', 'client-users', user.id]);
            });
        }
        break;

      case 'edit':
        {
          this.userService
            .update(this.user.id, this.user).pipe(
            finalize(onComplete))
            .subscribe((user) => {
              this.user = user;

              this.notifier.success('Utilisateur client enregistré avec succès');

              // Got to view screen
              this.router.navigate(['shares-ledger', 'client-users', user.id]);
            });
        }
        break;
    }
  }

  cancel() {
    if (this.mode === 'edit') {
      this.router.navigate(['shares-ledger', 'client-users', this.user.id]);
    }
    else {
      if (this.user.deleted) {
        this.router.navigate(['shares-ledger', 'client-users', 'deleted']);
      }
      else {
        this.router.navigate(['shares-ledger', 'client-users']);
      }
    }
  }

  delete() {
    this.dialogService
      .confirmDelete('Voulez-vous supprimer cet utilisateur client?')
      .subscribe((result) => {
        if (!result) {
          return;
        }

        this.notifier.startProgress();

        this.userService
          .delete(this.user.id)
          .subscribe((id) => {
            this.notifier.success('Utilisateur client supprimé avec succès');

            this.router.navigate(['shares-ledger', 'client-users']);

          }, (err) => {
            this.notifier.endProgress();
          });
      });
    
    return false;
  }

  restore(user: CLUser) {
    this.dialogService
      .confirm({
        title: 'Restauration',
        message: 'Voulez-vous restaurer cet utilisateur client?',
        confirm_button_text: 'Restaurer',
        confirm_button_class: 'btn-primary'
      })
      .subscribe((result) => {
        if (!result) {
          return;
        }

        this.notifier.startProgress();

        this.userService
          .restore(this.user.id)
          .subscribe((id) => {
            this.notifier.success('Utilisateur client restauré avec succès');

            this.router.navigate(['shares-ledger', 'client-users', 'deleted']);

          }, (err) => {
            this.notifier.endProgress();
          });
      });
    
    return false;
  }

  resendEmailVerification() {

    let email = this.user.latestEmailVerification ? this.user.latestEmailVerification.email : this.user.username;

    this.notifier.startProgress();

    this.userService
      .resendEmailVerification(this.user.id, email)
      .subscribe((result) => {
        if (result) {
          this.notifier.success(`Un message d'invitation a été envoyé à l'adresse ${email}`);

          this.router.navigate(['shares-ledger', 'client-users', this.user.id]);
        }

        this.notifier.endProgress();
      }, (err) => {
        this.notifier.endProgress();
      });

    return false;
  }

  lookupAccount(event: Event) {
    this.dialogService.show({
      component: CLAccountSearchDialogComponent,
      props: {

        onClose: () => {
          this.dialogService.hide();
        },

        onSelect: (account: CLAccount) => {
          this.dialogService.hide();

          this.user.account = account;
        }
      }
    });

    event.preventDefault();
  }

  showCLAccount() {
    if (this.user.account.deleted) {
      this.router.navigate(['shares-ledger', 'client-accounts', 'deleted', this.user.account.id]);
    }
    else {
      this.router.navigate(['shares-ledger', 'client-accounts', this.user.account.id]);
    }

    event.preventDefault;
  }
}
